import React from 'react';
import ReactDOM from 'react-dom/client';
import { supabase } from '../core/supabase.js';
import { ORIGIN } from '../core/config.js';
import { isNative, pageUrl, platform } from '../core/platform.js';
import { forgetPushDevice } from '../core/push.js';
import { nativeProviders, nativeSignIn, nativeSignOut } from '../core/native-auth.js';
import { NativeSSO } from '../ui/native-sso.jsx';
import { heroSay } from '../ui/auth-hero.js';
/* Invite links always point at domovoi.ca. Auth redirects (OAuth, password
   reset) still use this page's origin: they are web-only flows, and previews
   must come back to themselves. The app signs in natively instead (D2.3):
   Apple and Google through the phone's own sheet (core/native-auth.js). */
/* ============================================================
   Domovoi · Sign-in
   Modes: email/password, magic link, Google SSO (web); Apple and Google (app)
   Screens: signin → (forgot password) → (magic link sent) → redirect
   ============================================================ */

const { useState, useEffect, useRef } = React;

const SUPABASE_URL = 'https://api.domovoi.ca';
const SUPABASE_KEY = 'sb_publishable_gcoyQzFgdG_maC7P5AjrvA_-xSRI3ka';
// Read before the client starts: it takes the tokens out of the address and
// signs you in, and only afterwards (a tick later) says it was a reset.
const ARRIVED_TO_RESET = /(^|[#&])type=recovery(&|$)/.test(window.location.hash);
const sb = supabase();   // the shared client (src/core/supabase.js)

// After sign-in, where to send the user. If they have an incomplete
// onboarding_step we resume the registration flow, otherwise go home.
// ?next=/curated/ (same-site paths only) sends you back where you came from.
const HOME = (function () {
  const next = new URLSearchParams(window.location.search).get('next') || '';
  // The app shell too (D2.1): /shell.html, optionally reopening a product tab.
  const shell = /^\/shell\.html(\?open=\/(catered|curated|considered|courted)\/[\w\-\/]*)?$/.test(next);
  return shell || /^\/(?!\/)[\w\-\/]*(#[\w\-]+)?$/.test(next) ? next : '/';
})();

// ---- Icons (shared with join/app.jsx, inlined here for independence) ----

const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
  </svg>
);
const IconEye = ({ open }) => open ? (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
) : (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const IconChevron = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>
);
const IconMail = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5.5" width="18" height="13" rx="3" fill="none" stroke="currentColor" strokeWidth="1.9"/><path d="m4 7.5 8 6 8-6" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round"/></svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5 9.8 17.3 19 7.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
);
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);
function Spinner() { return <span className="reg-spinner" />; }
function RegError({ msg }) {
  if (!msg) return null;
  return <div className="reg-error" role="alert">{msg}</div>;
}
function Eyebrow({ children }) {
  return (
    <div className="reg-eyebrow">
      <span className="reg-eyebrow-dot" />
      {children}
    </div>
  );
}

// ---- After sign-in: redirect based on onboarding state ----

let tracked = false;   // onAuthStateChange and getSession can both land here
async function redirectAfterSignIn(user) {
  if (window.DVIdentify) window.DVIdentify(user.id, { email: user.email });
  if (window.DVTrack && !tracked) {
    tracked = true;
    // Google on the sign-in page creates the account if there isn't one, so
    // a brand-new user can arrive here without passing through /join/.
    const isNew = user.created_at && Date.now() - new Date(user.created_at).getTime() < 5 * 60 * 1000
      && (!user.last_sign_in_at || Math.abs(new Date(user.last_sign_in_at) - new Date(user.created_at)) < 60 * 1000);
    if (isNew) window.DVTrack('signed_up', { method: 'oauth', via_invite: false, from: 'signin' });
    else window.DVTrack('signed_in');
  }
  const { data: profile } = await sb
    .from('profiles')
    .select('onboarding_step, first_name')
    .eq('id', user.id)
    .single();

  // Cache who this is, so the nav on the marketing pages can render an
  // avatar without loading the Supabase SDK.
  if (window.DVIdentity) {
    window.DVIdentity.save({ name: profile?.first_name || null, email: user.email || null });
  }

  const step = profile?.onboarding_step;
  if (!step || step === 'name' || step === 'invite') {
    // Resume onboarding
    window.location.href = pageUrl(HOME !== '/' ? '/join/?next=' + encodeURIComponent(HOME) : '/join/');
  } else {
    window.location.href = pageUrl(HOME);
  }
}

// ---- Screen: Sign in ----

function ScreenSignIn({ onForgot, initialEmail }) {
  const [email, setEmail]       = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const ready = emailValid && password.length >= 1;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!ready) return;
    setError(''); setLoading(true);
    try {
      const { data, error: err } = await sb.auth.signInWithPassword({ email, password });
      if (err) throw err;
      await redirectAfterSignIn(data.user);
    } catch (err) {
      const msg = err.message || '';
      if (msg.toLowerCase().includes('invalid login')) {
        setError('Wrong email or password.');
      } else if (msg.toLowerCase().includes('email not confirmed')) {
        setError('Check your email — you need to confirm your address before signing in.');
      } else {
        setError(msg || 'Something went wrong — try again.');
      }
    }
    setLoading(false);
  }

  async function handleSSO(provider) {
    const { error: err } = await sb.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.href }
    });
    if (err) setError(err.message);
  }

  // In the app: the phone's own Apple or Google sheet, then straight on.
  const [nativeBusy, setNativeBusy] = useState(null);
  async function handleNative(provider) {
    setError(''); setNativeBusy(provider);
    try {
      const data = await nativeSignIn(provider);
      if (data && data.user) { await redirectAfterSignIn(data.user); return; }
    } catch (err) { setError(err.message); }
    setNativeBusy(null);
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Eyebrow>Welcome back</Eyebrow>
      <h1 className="reg-heading">Sign in</h1>

      {/* In the app, a web redirect can't come back: the phone signs you in (D2.3). */}
      {isNative() ? <NativeSSO providers={nativeProviders()} onPick={handleNative} busy={nativeBusy} /> : (
      <div className="reg-sso">
        <button type="button" className="reg-sso-btn" onClick={() => handleSSO('google')}>
          <GoogleLogo /> Continue with Google
        </button>
      </div>)}

      <div className="reg-divider">or with email</div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="si-email">Email</label>
        <input
          id="si-email" className="reg-input" type="email"
          placeholder="you@home.com" value={email}
          onChange={e => setEmail(e.target.value)}
          autoComplete="email" autoFocus required
        />
      </div>

      <div className="reg-field">
        <label className="reg-label" htmlFor="si-password">Password</label>
        <div className="reg-input-wrap">
          <input
            id="si-password" className="reg-input"
            type={showPw ? 'text' : 'password'}
            placeholder="Your password" value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password" required
          />
          <button
            type="button" className="reg-input-toggle"
            onClick={() => setShowPw(v => !v)}
            aria-label={showPw ? 'Hide password' : 'Show password'}
          >
            <IconEye open={showPw} />
          </button>
        </div>
      </div>
      <div className="reg-forgot"><button type="button" className="reg-link" onClick={() => onForgot(email)}>Forgot password?</button></div>

      <RegError msg={error} />

      <button type="submit" className="reg-btn" disabled={!ready || loading}>
        {loading ? <Spinner /> : <>Sign in <IconArrow /></>}
      </button>

      <p className="reg-hint">New here? <a href={HOME !== '/' ? '/join/?next=' + encodeURIComponent(HOME) : '/join/'}>Create account</a></p>
    </form>
  );
}

// ---- Screen: Forgot password ----

function ScreenForgot({ onBack, initialEmail }) {
  const [email, setEmail]   = useState(initialEmail || '');
  const [sent, setSent]     = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  async function handleSubmit(e) {
    if (e) e.preventDefault();
    if (!emailValid) return;
    setError(''); setLoading(true);
    const { error: err } = await sb.auth.resetPasswordForEmail(email, {
      // Inside the app the page's origin isn't a web address an email can link to.
      redirectTo: `${isNative() ? ORIGIN : window.location.origin}/signin/`
    });
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    setSent(true);
    setLoading(false);
  }

  if (sent) {
    // In the iPhone app, Open Mail opens the Mail app (Capacitor hands message:// to iOS); the link opens on this phone.
    const mail = platform() === 'ios';
    return (
      <div className="reg-magic-sent">
        <button type="button" className="reg-back au-sent-back" onClick={onBack} aria-label="Back"><IconChevron /></button>
        <div className="reg-magic-sent-icon"><IconMail /></div>
        <h1 className="reg-heading">Check your email</h1>
        <p className="reg-sub">
          We sent a link to <strong>{email}</strong>. {mail ? 'Open it on this phone to choose a new password.' : 'Open it to choose a new password.'}
        </p>
        <div className="au-acts">
          {mail ? <a className="reg-btn" href="message://" onClick={() => { if (window.DVTrack) window.DVTrack('password_reset_mail_opened', { product: 'account' }); }}>Open Mail</a> : null}
          <button type="button" className={'reg-btn ' + (mail ? 'reg-btn-ghost' : 'reg-btn-soft')} disabled={loading} onClick={() => handleSubmit()}>{loading ? <Spinner /> : 'Send it again'}</button>
          <p className="reg-hint">
            <button type="button" onClick={onBack}>Back to sign in</button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <button
        type="button" className="reg-back" onClick={onBack}
        style={{ marginBottom: 24 }}
      >
        <IconChevron />
        Back
      </button>

      <Eyebrow>Password reset</Eyebrow>
      <h1 className="reg-heading">Forgotten your password?</h1>
      <p className="reg-sub">We’ll email you a link to choose a new one.</p>

      <div className="reg-field">
        <label className="reg-label" htmlFor="fp-email">Email</label>
        <input
          id="fp-email" className="reg-input" type="email"
          placeholder="you@home.com" value={email}
          onChange={e => setEmail(e.target.value)}
          autoComplete="email" autoFocus required
        />
      </div>

      <RegError msg={error} />

      <button type="submit" className="reg-btn" disabled={!emailValid || loading} style={{ marginTop: 8 }}>
        {loading ? <Spinner /> : <>Send the link <IconArrow /></>}
      </button>
    </form>
  );
}

// ---- Screen: Reset password (landed from email link) ----

function ScreenReset() {
  const [who, setWho]           = useState('');
  useEffect(() => { sb.auth.getSession().then(({ data: { session } }) => { if (session && session.user) setWho(session.user.email || ''); }); }, []);
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [done, setDone]         = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) return;
    setError(''); setLoading(true);
    const { data, error: err } = await sb.auth.updateUser({ password });
    if (err) { setError(err.message); setLoading(false); return; }
    setDone(true);
    setTimeout(() => redirectAfterSignIn(data.user), 1800);
  }

  if (done) {
    return (
      <div className="reg-magic-sent">
        <div className="reg-magic-sent-icon"><IconCheck /></div>
        <h1 className="reg-heading" style={{ textAlign: 'center' }}>Password updated</h1>
        <p className="reg-sub" style={{ textAlign: 'center' }}>Taking you home…</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Eyebrow>New password</Eyebrow>
      <h1 className="reg-heading">Choose a new password</h1>
      <p className="reg-sub">{who ? <>For <strong>{who}</strong>.</> : 'At least 8 characters.'}</p>

      <div className="reg-field">
        <label className="reg-label" htmlFor="rp-password">New password</label>
        <div className="reg-input-wrap">
          <input
            id="rp-password" className="reg-input"
            type={showPw ? 'text' : 'password'}
            placeholder="At least 8 characters"
            value={password} onChange={e => setPassword(e.target.value)}
            autoComplete="new-password" autoFocus minLength={8} required
          />
          <button
            type="button" className="reg-input-toggle"
            onClick={() => setShowPw(v => !v)}
            aria-label={showPw ? 'Hide password' : 'Show password'}
          >
            <IconEye open={showPw} />
          </button>
        </div>
      </div>

      <RegError msg={error} />

      <button type="submit" className="reg-btn" disabled={password.length < 8 || loading} style={{ marginTop: 8 }}>
        {loading ? <Spinner /> : <>Set password <IconArrow /></>}
      </button>
    </form>
  );
}

// ---- App ----

function App() {
  const [screen, setScreen]     = useState('signin'); // 'signin' | 'forgot' | 'reset' | 'signout' | 'deleted'
  const [typed, setTyped]       = useState('');       // the email typed on sign in carries over to Forgot
  // The plum panel's line for each screen (src/ui/auth-hero.js)
  useEffect(() => {
    const SAY = { signin: 'Welcome back.', forgot: 'Welcome back.', reset: 'Nearly back in.', signout: 'See you soon.', deleted: 'Your account is gone.' };
    heroSay(SAY[screen] || 'Welcome back.', { mode: screen === 'deleted' ? 'gone' : screen === 'forgot' ? 'forgot' : '' });
  }, [screen]);
  const [booting, setBooting]   = useState(true);
  const resetting = useRef(ARRIVED_TO_RESET);

  useEffect(() => {
    // Arriving from the account menu: sign out for real, then say so. Doing it
    // here means the token is revoked server-side, not just dropped locally.
    // ?deleted=1: the account page has just deleted your account (D2.9); tidy
    // this device the same way, and say so.
    const q = new URLSearchParams(window.location.search);
    const deleted = !!q.get('deleted');
    if (q.get('signout') || deleted) {
      (async () => {
        if (!deleted) await forgetPushDevice();   // this phone stops getting your notifications
        await nativeSignOut({ forget: deleted }); // and the next person gets Apple's or Google's account picker
        try { await sb.auth.signOut(deleted ? { scope: 'local' } : undefined); } catch (e) {}
        if (window.DVTrackReset) window.DVTrackReset();
        if (window.DVIdentity) window.DVIdentity.clear();
        if (window.DVPwa) await window.DVPwa.clearData();
        try { localStorage.removeItem('domovoi.considered.cal.v1'); } catch (e) {}   // Considered's calendar as last seen (V4)
        window.history.replaceState({}, '', '/signin/');
        setScreen(deleted ? 'deleted' : 'signout');
        setBooting(false);
      })();
      return;
    }

    // A reset link signs you in before you choose the new password; stay on
    // the reset screen until you have, whatever order the SDK reports things in.
    const { data: { subscription } } = sb.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        resetting.current = true;
        setScreen('reset');
        setBooting(false);
        return;
      }
      if (event === 'SIGNED_IN' && session) {
        if (!resetting.current) await redirectAfterSignIn(session.user);
        return;
      }
      setBooting(false);
    });

    // Check existing session on load
    sb.auth.getSession().then(({ data: { session } }) => {
      if (session && resetting.current) { setScreen('reset'); setBooting(false); return; }
      if (session) { redirectAfterSignIn(session.user); return; }
      setBooting(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (booting) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className="reg-card">
      {screen === 'signout' && (
        <div>
          <Eyebrow>Signed out</Eyebrow>
          <h1 className="reg-heading">That’s you signed out</h1>
          <p className="reg-sub">
            Anything you plan in this browser stays here, just for you, until you sign back in.
          </p>
          <button type="button" className="reg-btn" onClick={() => setScreen('signin')}>
            Sign back in <IconArrow />
          </button>
          <a className="reg-btn reg-btn-ghost" href="/">Back to Domovoi</a>
        </div>
      )}
      {screen === 'deleted' && (
        <div className="au-gone">
          <Eyebrow>Account deleted</Eyebrow>
          <p className="reg-sub">Thanks for giving Domovoi a go. You’re welcome back any time.</p>
          <div className="au-acts">
            <a className="reg-btn" href={pageUrl('/join/')}>
              Make a new account <IconArrow />
            </a>
            <button type="button" className="reg-btn reg-btn-ghost" onClick={() => setScreen('signin')}>Sign in</button>
          </div>
        </div>
      )}
      {screen === 'signin' && (
        <ScreenSignIn
          initialEmail={typed}
          onForgot={(email) => { setTyped(email || ''); setScreen('forgot'); }}
        />
      )}
      {screen === 'forgot' && (
        <ScreenForgot initialEmail={typed} onBack={() => setScreen('signin')} />
      )}
      {screen === 'reset' && (
        <ScreenReset />
      )}
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <div className="reg-main">
    <App />
  </div>
);
